import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import type { CountryModel } from '../../../../typings/api/skymap/rest/v0/.common';
import type { GetCompanyResponse as GetCompanyResponseV0 } from '../../../../typings/api/skymap/rest/v0/company';
import type { GetCompanyResponse as GetCompanyResponseV1 } from '../../../../typings/api/skymap/rest/v1/company';
import { SkyMapAxiosServiceFactory } from '../../services/axios/skymap-axios-service-factory';
import { isDefined } from '../../utils/variables';
import { useAppContext, UserRole } from './app-state';

// Company information rarely changes, so we can cache for a while.
const CACHE_EXPIRATION_MS = 10 * 60 * 1000;

/** Company shape used in state: v0-compatible with country as CountryModel from v1 API. */
export type GetCompanyResponse = Omit<GetCompanyResponseV0, 'country'> & {
  country?: CountryModel;
};

export type CompanyStatus = 'idle' | 'loading' | 'ready' | 'error';

export type CompanyStateContextValue = {
  company: GetCompanyResponse | undefined;
  status: CompanyStatus;
  error: Error | undefined;
  fetchCompany: (companyId: string) => Promise<GetCompanyResponse | undefined>;
  getCompanyCountry: () => CountryModel | undefined;
  removeCompanyAdmin: (userId: string) => Promise<void>;
  reset: () => void;
};

export const CompanyStateContext = React.createContext<CompanyStateContextValue | undefined>(
  undefined,
);

const companyQueryKey = (companyId: string) => ['company', companyId] as const;

const COMPANY_INCLUDE_COUNTRY = ['company.country'] as const;

/**
 * Fetches company by running v1 and v0 getCompany in parallel, then merging.
 * - v1: company base + country as CountryModel (include: ['company.country']).
 * - v0: companyAdmins and licenses (v1 does not return these).
 *
 * NOTE: Ideally licenses and company admins would be loaded via separate endpoints
 * (e.g. getCompanyLicenses, getCompanyAdmins) rather than bundled in getCompany.
 * This merge is a migration step toward v1; when those endpoints exist, we can
 * drop the v0 getCompany call and fetch admins/licenses separately.
 */
async function fetchCompanyApi(companyId: string): Promise<GetCompanyResponse> {
  const factory = SkyMapAxiosServiceFactory.instance;
  /* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- Promise.all tuple and v1 response.data; types are correct */
  const [v1Response, v0Response]: [GetCompanyResponseV1, GetCompanyResponseV0] = await Promise.all([
    factory.createCompanyServiceV1().getCompany({
      path: { companyId },
      query: { include: [...COMPANY_INCLUDE_COUNTRY] },
    }),
    factory.createCompanyServiceV0().getCompany({ path: { companyId } }),
  ]);

  const country = v1Response.data?.country;
  const merged: GetCompanyResponse = {
    ...v0Response,
    country,
  };
  /* eslint-enable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access */
  return merged;
}

type Props = {
  children: React.ReactNode;
  /**
   * Optional context value override. When provided, this value is used instead of internal state.
   * Useful in tests to inject a specific company and mock handlers (see geodata-upload.test.tsx pattern).
   */
  value?: CompanyStateContextValue;
};

export const CompanyStateProvider = ({ children, value: valueOverride }: Props) => {
  const queryClient = useQueryClient();
  const { currentUser } = useAppContext();
  const [currentCompanyId, setCurrentCompanyId] = useState<string | undefined>();

  const isProjectUser = currentUser?.selectedRole === UserRole.ProjectUser;
  const shouldFetchCompany =
    isDefined(currentCompanyId) && currentCompanyId.length > 0 && !isProjectUser;

  useEffect(() => {
    if (isProjectUser) {
      setCurrentCompanyId(undefined);
    }
  }, [isProjectUser]);

  const query = useQuery({
    queryKey: companyQueryKey(currentCompanyId ?? '__disabled__'),
    queryFn: () =>
      currentCompanyId
        ? fetchCompanyApi(currentCompanyId)
        : Promise.reject(new Error('No company ID')),
    staleTime: CACHE_EXPIRATION_MS,
    enabled: shouldFetchCompany,
  });

  const company = query.data;

  const status = useMemo((): CompanyStatus => {
    if (!isDefined(currentCompanyId)) {
      return 'idle';
    }
    if (query.isPending) {
      return 'loading';
    }
    if (query.isError) {
      return 'error';
    }
    return 'ready';
  }, [currentCompanyId, query.isPending, query.isError]);

  const error = useMemo(
    () => (query.error instanceof Error ? query.error : undefined),
    [query.error],
  );

  const reset = useCallback(() => {
    setCurrentCompanyId(undefined);
  }, []);

  const fetchCompany = useCallback(
    async (companyId: string): Promise<GetCompanyResponse | undefined> => {
      if (currentUser?.selectedRole === UserRole.ProjectUser) {
        setCurrentCompanyId(undefined);
        return;
      }
      const data = await queryClient.fetchQuery({
        queryKey: companyQueryKey(companyId),
        queryFn: () => fetchCompanyApi(companyId),
        staleTime: CACHE_EXPIRATION_MS,
      });
      setCurrentCompanyId(companyId);
      return data;
    },
    [queryClient, currentUser?.selectedRole],
  );

  const removeCompanyAdminMutation = useMutation({
    mutationFn: async ({ companyId, userId }: { companyId: string; userId: string }) => {
      await SkyMapAxiosServiceFactory.instance.createCompanyServiceV0().deleteCompanyAdmin({
        path: { companyId, userId },
      });
    },
    onSuccess: (_data, { companyId }) => {
      void queryClient.invalidateQueries({ queryKey: companyQueryKey(companyId) });
      void queryClient.refetchQueries({ queryKey: companyQueryKey(companyId) });
    },
  });

  const removeCompanyAdmin = useCallback(
    async (userId: string): Promise<void> => {
      if (!company) {
        throw new Error('Company is not loaded');
      }
      await removeCompanyAdminMutation.mutateAsync({
        companyId: company.id,
        userId,
      });
    },
    [company, removeCompanyAdminMutation],
  );

  const getCompanyCountry = useCallback((): CountryModel | undefined => {
    return company?.country;
  }, [company?.country]);

  const value = useMemo<CompanyStateContextValue>(
    () => ({
      company,
      status,
      error,
      fetchCompany,
      getCompanyCountry,
      removeCompanyAdmin,
      reset,
    }),
    [company, status, error, fetchCompany, getCompanyCountry, removeCompanyAdmin, reset],
  );

  const contextValue = valueOverride ?? value;

  return (
    <CompanyStateContext.Provider value={contextValue}>{children}</CompanyStateContext.Provider>
  );
};

export const useCompanyState = (): CompanyStateContextValue => {
  const context = React.useContext(CompanyStateContext);
  if (context === undefined) {
    throw new Error('useCompanyState must be used within a CompanyStateProvider');
  }
  return context;
};

/**
 * Returns the current company. Throws if company is not loaded.
 * Use in components that only render when we're on a company (or project-with-company) context.
 */
export const useCompany = (): GetCompanyResponse => {
  const { company } = useCompanyState();
  if (!isDefined(company)) {
    throw new Error(
      'Company is not loaded. Ensure the component is rendered within a company or project context.',
    );
  }
  return company;
};
